import { CircularProgress, Radio, Typography } from '@material-ui/core'

import { getTranslate } from '../../../../localization'
import { AdvertisedPromotion } from '../../../../types/promotion'
import useStyles from './styles'

/**
 * Percent discounts arrive as basis points (1700 = 17.00%), matching the promotion
 * service's own representation. Amount discounts arrive as minor units, which need a
 * currency to render, so they are shown as-is rather than guessed at here.
 */
export const describeDiscount = (promotion: AdvertisedPromotion): string => {
  if (promotion.discountType === 'Percent') {
    return `${Number(promotion.discountValue) / 100}%`
  }
  return `${promotion.discountValue} ${promotion.currency}`
}

/** One selectable offer. */
const OfferRow = ({
  offer,
  selected,
  disabled,
  onSelect
}: {
  offer: AdvertisedPromotion
  selected: boolean
  disabled: boolean
  onSelect: (offer: AdvertisedPromotion) => void
}) => {
  const classes = useStyles()

  return (
    <label className={classes.offerRow}>
      <Radio
        checked={selected}
        color="primary"
        disabled={disabled}
        inputProps={{ 'aria-label': offer.name }}
        // onClick, not onChange: a radio does not fire change when it is already checked,
        // so re-picking the selected offer to clear it would be unreachable. The panel
        // learned this from a test that expected otherwise.
        onClick={() => onSelect(offer)}
      />
      <span className={classes.offerText}>
        <Typography variant="body1">
          {offer.name}
          {/* The rate, pulled out of the sentence so a column of offers compares at a
              glance. It is a RATE, not this cart's saving — only evaluatePromotion knows
              the money, and that is shown once the offer is applied. */}
          <span className={classes.offerDiscount}>
            {describeDiscount(offer)}
          </span>
        </Typography>
        <Typography variant="body2" className={classes.description}>
          {offer.requiresGuestIdentity
            ? getTranslate('Requires guest details')
            : getTranslate('Code {code}', { code: offer.code })}
        </Typography>
      </span>
    </label>
  )
}

type OfferListProps = {
  offers: AdvertisedPromotion[]
  selectedCode: string
  disabled: boolean
  /** The lookup is still in flight. Only shown while there is nothing to list. */
  loading: boolean
  onSelect: (offer: AdvertisedPromotion) => void
}

/** The advertised offers a booker can pick instead of typing a code. */
const OfferList = ({
  offers,
  selectedCode,
  disabled,
  loading,
  onSelect
}: OfferListProps) => {
  const classes = useStyles()

  // Only while there is nothing to show: `cache-and-network` re-fetches in the background
  // on every mount, so a spinner keyed on `loading` alone would flash over a list the
  // booker is already reading.
  if (offers.length === 0) {
    if (loading) {
      return (
        <div className={classes.offersLoading}>
          <CircularProgress size={18} />
        </div>
      )
    }
    return null
  }

  return (
    <div className={classes.offers}>
      <Typography variant="body2" className={classes.offersLabel}>
        {getTranslate('Available offers')}
      </Typography>
      {offers.map((offer) => (
        <OfferRow
          key={offer.promotionId}
          offer={offer}
          selected={selectedCode === offer.code}
          disabled={disabled}
          onSelect={onSelect}
        />
      ))}
    </div>
  )
}

export default OfferList
